<template>
  <nav v-if="!loadingData">
    <AlertMessages v-if="alertValue" :alert-type="alertType" :alert-msg="alertMsg" @reset="alertValue = false" />
    <v-row>
      <v-col cols="11" class="mx-auto">
        <v-btn ref="back" text data-testid="back-button" class="text-none no-hover pa-0 ml-0" :ripple="false"
          color="greytext" @click="redirectToIndividualsList">
          <v-icon>mdi-chevron-left</v-icon>{{ $t('backToIndividualsList') }}
        </v-btn>

      </v-col>
    </v-row>
    <v-card elevation="0" data-testid="navigation-buttons" max-width="460px" class="mx-auto" color="background">
      <v-card-actions class="justify-center pa-0">
        <div class="px-6">
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn data-testid="prev-button" elevation="0" class="rounded-xxl text-none pa-0" v-on="on" color="accent"
                min-width="36px" @click="getPrevAndNext('prev')" :disabled="!showPrev">
                <v-icon size="24">
                  mdi-chevron-left
                </v-icon>
              </v-btn>
            </template>
            <span>{{ $t('prevPersonTooltip') }}</span>
          </v-tooltip>
        </div>
        <v-spacer class="text-center">
          <p class="ma-0">{{ userName }}</p>

        </v-spacer>
        <div class="px-6">
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn data-testid="prev-button" elevation="0" class="rounded-xxl text-none pa-0" v-on="on" color="accent"
                min-width="36px" @click="getPrevAndNext('next')" :disabled="!showNext">
                <v-icon size="24">
                  mdi-chevron-right
                </v-icon>
              </v-btn>
            </template>
            <span>{{ $t('nextPersonTooltip') }}</span>
          </v-tooltip>
        </div>
      </v-card-actions>
    </v-card>
    <v-row>
      <v-col cols="11" class="content-block" :class="!mobile() ? 'text-right' : 'text-center pa-6'">
        <v-btn ref="download" data-testid="download-pdf-button" color="accent" min-width="100"
          class="rounded-xxl text-none px-4" @click="downloadSurveyPDF()" :loading="downloadingPDF">
          {{ $t("downloadSurveyOverviewPDF") }} <v-icon class="pl-1"> mdi-download </v-icon>
        </v-btn>
      </v-col>
    </v-row>
    <section>
      <div v-for="(question, index) in allQuestions" :key="index" class="list-group-item mb-4">
        <div class="content-block question pa-12 rounded-xxl"
          :style="`background-color:` + question.bg_color">
          <div>
            <v-row v-if="question.type == 1" align="center" justify="center" data-testid="survey-1" no-gutters>
              <v-col cols="12" sm="12" md="12">
                <MultipleChoiceTextQuestionTextAnswer :question="question" :user="individual" :key="updateIndividual" />
              </v-col>
            </v-row>
            <v-row v-else-if="question.type == 2" align="center" justify="center" data-testid="survey-2" no-gutters>
              <v-col cols="12">
                <MultipleChoiceTextQuestionTextImageAnswer :question="question" :index="index" :user="individual"
                  :key="updateIndividual" />
              </v-col>
            </v-row>
            <v-row v-else-if="question.type == 3" align="center" justify="center" data-testid="survey-3" no-gutters>
              <v-col cols="12">
                <MultipleChoiceTextImageQuestionTextAnswer :question="question" :index="index" :user="individual"
                  :key="updateIndividual" />
              </v-col>
            </v-row>
            <v-row v-else-if="question.type == 4" align="center" justify="center" data-testid="survey-4" no-gutters>
              <v-col cols="12">
                <OpenText :question="question" :user="individual" :key="updateIndividual" />
              </v-col>
            </v-row>
            <v-row v-else-if="question.type == 5" align="center" justify="center" data-testid="survey-5" no-gutters>
              <v-col cols="12">
                <OpenTextAttachment :question="question" :user="individual" :key="updateIndividual" />
              </v-col>
            </v-row>
            <v-row v-else-if="question.type == 6" align="center" justify="center" data-testid="survey-6" no-gutters>
              <v-col cols="12">
                <RatingStars :question="question" :user="individual" :key="updateIndividual" />
              </v-col>
            </v-row>
            <v-row v-else-if="question.type == 7" align="center" justify="center" data-testid="survey-7" no-gutters>
              <v-col cols="12">
                <RatingNumbers :question="question" :user="individual" :key="updateIndividual" />
              </v-col>
            </v-row>
            <v-row v-else-if="question.type == 8" align="center" justify="center" data-testid="survey-8" no-gutters>
              <v-col cols="12">
                <RatingNumbersComment :question="question" :user="individual" :key="updateIndividual" />
              </v-col>
            </v-row>
          </div>
        </div>
      </div>
    </section>
    <section id="survey-pdf-content" class="only-for-printing" style="position: fixed; top: -999999999999px;">
      <v-app style="background-color: transparent;">
        <div v-for="(question, index) in allQuestions" :key="index" class="list-group-item">
          <div class="content-block question pa-12 rounded-xxl replace-box-shadow"
            :style="`background-color:` + question.bg_color">
            <div>
              <v-row v-if="question.type == 1" align="center" justify="center" data-testid="survey-1" no-gutters>
                <v-col cols="12" sm="12" md="12">
                  <MultipleChoiceTextQuestionTextAnswer :question="question" :user="individual" :key="updateIndividual"
                    :pdf="true" />
                </v-col>
              </v-row>
              <v-row v-else-if="question.type == 2" align="center" justify="center" data-testid="survey-2" no-gutters>
                <v-col cols="12">
                  <MultipleChoiceTextQuestionTextImageAnswer :question="question" :index="index" :user="individual"
                    :key="updateIndividual" />
                </v-col>
              </v-row>
              <v-row v-else-if="question.type == 3" align="center" justify="center" data-testid="survey-3" no-gutters>
                <v-col cols="12">
                  <MultipleChoiceTextImageQuestionTextAnswer :question="question" :index="index" :user="individual"
                    :key="updateIndividual" :pdf="true" />
                </v-col>
              </v-row>
              <v-row v-else-if="question.type == 4" align="center" justify="center" data-testid="survey-4" no-gutters>
                <v-col cols="12">
                  <OpenText :question="question" :user="individual" :key="updateIndividual" :pdf="true" />
                </v-col>
              </v-row>
              <v-row v-else-if="question.type == 5" align="center" justify="center" data-testid="survey-5" no-gutters>
                <v-col cols="12">
                  <OpenTextAttachment :question="question" :user="individual" :key="updateIndividual" :pdf="true" />
                </v-col>
              </v-row>
              <v-row v-else-if="question.type == 6" align="center" justify="center" data-testid="survey-6" no-gutters>
                <v-col cols="12">
                  <RatingStars :question="question" :user="individual" :key="updateIndividual" />
                </v-col>
              </v-row>
              <v-row v-else-if="question.type == 7" align="center" justify="center" data-testid="survey-7" no-gutters>
                <v-col cols="12">
                  <RatingNumbers :question="question" :user="individual" :key="updateIndividual" />
                </v-col>
              </v-row>
              <v-row v-else-if="question.type == 8" align="center" justify="center" data-testid="survey-8" no-gutters>
                <v-col cols="12">
                  <RatingNumbersComment :question="question" :user="individual" :key="updateIndividual" :pdf="true" />
                </v-col>
              </v-row>
            </div>
          </div>
        </div>
      </v-app>
    </section>
  </nav>
  <nav v-else class="fill-height d-flex align-center justify-center" style="position: fixed; top: 0; width: 100%;">
      <v-progress-circular
        size="125"
        indeterminate
        class=""
        color="grey"
      />
  </nav>
</template>
<script>
import i18n from "../../plugins/i18n";
import AlertMessages from "../../components/AlertMessages";
import { repeatedMethodsMixin } from "../../mixins/repeatedMethodsMixin";
import {
  getSurveyQuestions,
  getImageURLsInActivitySurvey,
  getIndividualAnswers,
  getAttemptsInSurvey
} from "../../api/activityApi";
import MultipleChoiceTextQuestionTextAnswer from "../../components/editor_components/survey/MultipleChoiceTextQuestionTextAnswer";
import MultipleChoiceTextQuestionTextImageAnswer from "../../components/editor_components/survey/MultipleChoiceTextQuestionTextImageAnswer";
import MultipleChoiceTextImageQuestionTextAnswer from "../../components/editor_components/survey/MultipleChoiceTextImageQuestionTextAnswer";
import RatingNumbersComment from "../../components/editor_components/survey/RatingNumbersComment";
import OpenText from "../../components/editor_components/survey/OpenText";
import OpenTextAttachment from "../../components/editor_components/survey/OpenTextAttachment";
import RatingNumbers from "../../components/editor_components/survey/RatingNumbers";
import RatingStars from "../../components/editor_components/survey/RatingStars";
import html2pdf from "html2pdf.js"

export default {
  components: {
    AlertMessages,
    MultipleChoiceTextQuestionTextAnswer,
    MultipleChoiceTextQuestionTextImageAnswer,
    MultipleChoiceTextImageQuestionTextAnswer,
    RatingNumbersComment,
    OpenText,
    OpenTextAttachment,
    RatingNumbers,
    RatingStars,
  },
  mixins: [repeatedMethodsMixin],
  data() {
    return {
      surveyId: this.$route.params.surveyId,
      alertMsg: "",
      alertType: "",
      alertValue: false,
      spaceId: window.$cookies.get("space_id"),
      loadingData: false,
      surveys: [],
      numbSubmissions: 0,
      allQuestions: [],
      individual: {},
      users: [],
      updateIndividual: '',
      attemptId: this.$route.params.attemptId,
      activityId: this.$route.params.id,
      userName: '',
      showNext: false,
      showPrev: false,
      downloadingPDF: false
    };
  },
  async created() {
    this.loadingData = true;
    await this.getSurveyQuestions();
    await this.fetchAttemptsInActivity();
    await this.userSurveyDetail(this.attemptId);
    this.loadingData = false;
  },
  methods: {
    async getSurveyQuestions() {
      try {
        const response = await getSurveyQuestions(this.surveyId);
        if (response) {
          if (response.status == 200) {
            for (const value of response.data.data) {
              if (
                value.attributes.type == 1 ||
                value.attributes.type == 2 ||
                value.attributes.type == 3
              ) {
                var responseQuestionImageURL = await this.changeImageURLs(
                  value.attributes.image
                );
                var options = [];
                for (const answer of value.attributes.options) {
                  var responseAnswerImageURL = await this.changeImageURLs(
                    answer.image
                  );
                  options.push({
                    aid: answer.aid,
                    option: answer.option,
                    image: answer.image,
                    imageDisplay: responseAnswerImageURL,
                  });
                }

                this.surveys.push({
                  qid: value.id,
                  id: value.id,
                  question: value.attributes.question,
                  description: value.attributes.description,
                  image: value.attributes.image,
                  imageDisplay: responseQuestionImageURL,
                  bg_color: value.attributes.bg_color,
                  allowed_options: value.attributes.allowed_options,
                  type: value.attributes.type,
                  options: options,
                });
              } else if (
                value.attributes.type == 4 ||
                value.attributes.type == 5
              ) {
                this.surveys.push({
                  qid: value.id,
                  id: value.id,
                  question: value.attributes.question,
                  description: value.attributes.description,
                  image: null,
                  imageDisplay: null,
                  bg_color: value.attributes.bg_color,
                  type: value.attributes.type,
                });
              } else {
                this.surveys.push({
                  qid: value.id,
                  id: value.id,
                  question: value.attributes.question,
                  description: value.attributes.description,
                  image: null,
                  imageDisplay: null,
                  bg_color: value.attributes.bg_color,
                  allowed_options: value.attributes.allowed_options,
                  type: value.attributes.type,
                });
              }
            }
            //created new array that will contains all question, start screen and endscreen. For carousel scroll effect
            this.allQuestions = JSON.parse(JSON.stringify(this.surveys));
          }
        }
      } catch (e) {
        if (e.response) {
          this.alertValue = true;
          this.alertType = "error";
          this.alertMsg = i18n.t("serverError");
        }
      }
    },
    async changeImageURLs(value) {
      var imagesToCheck = [];
      if (value != null) {
        imagesToCheck = [value];

        let dataImage = {
          data: {
            type: "surveys",
            id: this.surveyId,
            attributes: {
              paths: imagesToCheck,
              space_id: this.spaceId,
            },
          },
        };

        const responseImageQuestion = await getImageURLsInActivitySurvey(
          this.surveyId,
          dataImage
        );
        return responseImageQuestion.data.meta.files[value];
      } else {
        return null;
      }
    },
    async userSurveyDetail(attemptId) {
      //check if attempt id exists for this survey
      const validAttemptId = this.users.findIndex((a) => a.id === attemptId)
      if (validAttemptId == -1) {
        this.$router.push({ name: "PageNotFound" });
      } else {
        try {
          const response = await getIndividualAnswers(
            attemptId
          );
          if (response.status) {
            if (response.status == 200) {
              if (response.data.data.attributes.submitted_by) {
                this.userName = response.data.data.attributes.submitted_by
              } else {
                this.userName = 'Anonymous ' + (validAttemptId + 1)
              }

              this.userDetail =
                response.data.data.relationships["survey-question-attempts"].meta;
              for (const questionId in this.userDetail) {
                const questionData = this.userDetail[questionId];
                this.individual[questionId] = {
                  qid: questionId,
                  id: questionId,
                  answer: questionData,
                };
              }
              this.updateIndividual = Date.now()
            }
          }
        } catch (e) {
          if (e.response) {
            this.alertValue = true;
            if (e.response.status == 403) {
              this.$router.push({ name: "PageAccessDenied" });
            } else if (e.response.status == 404) {
              this.$router.push({ name: "PageNotFound" });
            } else {
              this.alertType = "error";
              this.alertMsg = i18n.t("serverError");
            }
          }
        }
      }

    },
    async fetchAttemptsInActivity() {
      try {
        const response = await getAttemptsInSurvey(this.surveyId);
        if (response.status) {
          if (response.status == 200) {
            this.users = response.data.data;
            const index = this.users.findIndex((a) => a.id === this.$route.params.attemptId)

            if (index < this.users.length - 1) {
              this.showNext = true
            }
            if (index > 0) {
              this.showPrev = true
            }
          }
        }
      } catch (e) {
        if (e.response) {
          this.alertValue = true;
          if (e.response.status == 403) {
            this.$router.push({ name: "PageAccessDenied" });
          } else if (e.response.status == 404) {
            this.$router.push({ name: "PageNotFound" });
          } else {
            this.alertType = "error";
            this.alertMsg = i18n.t("serverError");
          }
        }
      }
    },

    getPrevAndNext(action) {
      const index = this.users.findIndex((a) => a.id === this.$route.params.attemptId)
      if (index === -1) {
        return null
      }

      if (action == 'next') {
        this.showNext = false
        const next = this.users[index + 1]
        if (!next) {
          return null
        }
        this.$router.push({
          path:
            "/activity/" +
            this.activityId +
            "/survey/" +
            this.surveyId +
            "/individuals/" +
            next.id,
        });
        this.userSurveyDetail(next.id)
        this.updateIndividual = Date.now()

        if (index + 1 < this.users.length - 1) {
          this.showNext = true
        }
        if (index + 1 > 0) {
          this.showPrev = true
        }

      } else {
        this.showPrev = false
        const prev = this.users[index - 1]
        if (!prev) {
          return null
        }

        this.$router.push({
          path:
            "/activity/" +
            this.activityId +
            "/survey/" +
            this.surveyId +
            "/individuals/" +
            prev.id,
        });
        this.userSurveyDetail(prev.id)
        this.updateIndividual = Date.now()

        if (index - 1 < this.users.length - 1) {
          this.showNext = true
        }
        if (index - 1 > 0) {
          this.showPrev = true
        }

      }

    },
    redirectToIndividualsList() {
      this.$router.push({
        path:
          "/activity/" +
          this.activityId +
          "/survey/" +
          this.surveyId +
          "/individuals"
      });
    },
    async downloadSurveyPDF() {
      this.downloadingPDF = true
      let element = document.getElementById('survey-pdf-content').firstChild;

      const styleElements = (selector, style, action) => {
        element.querySelectorAll(selector).forEach((item) => {
          item.style[action] = style;
        });
      };

      styleElements('.replace-box-shadow', '1px solid #CECDCD', 'border');
      styleElements('.replace-box-shadow-input .v-input__slot', '1px solid #CECDCD', 'border');

      element.querySelectorAll(".survey-attachment-icon-link").forEach((item) => {
        item.innerHTML = '<span style="font-style: italic; color: #7C8386; font-size: 14px;">' + i18n.t('fileAttachedSurveyPDF') + '</span>'
      });

      element.querySelectorAll('textarea').forEach((textArea) => {
        const div = document.createElement('div');
        div.innerText = textArea.value;
        div.style.padding = '4px 16px 8px 0';
        div.style.wordBreak = 'break-word';
        textArea.style.display = 'none';
        textArea.parentElement.append(div);
      });

      const createImageDiv = (img, height) => {
        const div = document.createElement('div');
        div.style.backgroundImage = `url(${img.src})`;
        div.style.backgroundSize = 'contain';
        div.style.height = `${height}px`;
        div.style.backgroundPosition = 'center';
        img.style.display = 'none';
        img.parentElement.append(div);
      };

      element.querySelectorAll('.survey-question-image-editor img').forEach((img) => {
        createImageDiv(img, 300);
      });

      element.querySelectorAll('.survey-answer-image-editor img').forEach((img) => {
        createImageDiv(img, 247);
      });

      // Add page break before every div with class list-group-item that can't fit on the same A4 page
      let pageHeight = 1122; // A4 page height in pixels, adjust as needed (A4 - 1122)
      let contentHeight = 0;

      let listGroupItems = element.querySelectorAll('.list-group-item');

      for (let i = 0; i < listGroupItems.length; i++) {
        let listItem = listGroupItems[i];
        let listItemHeight = listItem.scrollHeight + 40;
        listItem.style.padding = "20px 20px 0 20px"

        if ((contentHeight + listItemHeight) > pageHeight && i !== 0) {
          listItem.style.pageBreakBefore = "always";
          contentHeight = 0;
        }
        contentHeight += listItemHeight;
      }
      let opt = {
        margin: 0,
        filename: 'survey-individuals-' + this.userName.replace(/\s/g, "") + '.pdf',
        pagebreak: { mode: ['css', 'legacy', 'avoid-all'], avoid: ['img', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'a', 'span'] },
        image: { type: 'jpeg', quality: 0.95 },
        enableLinks: false,
        html2canvas: {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          imageTimeout: 0
        },
        jsPDF: { unit: 'px', hotfixes: ["px_scaling"] }
      };
      await html2pdf().set(opt).from(element).save();
      this.downloadingPDF = false
    },
  },
};
</script>
<style>
.col-sm-12 .v-list-item .survey-answer .v-text-field__slot {
  width: 0%;
}

.content-block.question {
  box-shadow: 0px 3px 1px -2px rgb(38 50 56 / 10%),
    0px 2px 2px 0px rgb(38 50 56 / 10%), 0px 1px 5px 0px rgb(38 50 56 / 10%);
}
</style>
